import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ClipboardList } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { AccountabilityDriEntry } from "@/types";
import { getPriorityStatusMeta } from "@/components/priorities/priorityCardTokens";

interface DriWorkloadSectionProps {
  entries: AccountabilityDriEntry[];
  isLoading: boolean;
}

/** DRIs ranked most→least by how many tasks they are directly responsible for. */
export function DriWorkloadSection({ entries, isLoading }: DriWorkloadSectionProps) {
  const [expandedPid, setExpandedPid] = useState<string | null>(null);

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-xl border border-border bg-muted/30" />;
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-8 text-center">
        <p className="text-sm font-medium text-muted-foreground">No DRIs on active priorities</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {entries.map(({ user, taskCount, tasks }) => (
        <Collapsible
          key={user.pid}
          open={expandedPid === user.pid}
          onOpenChange={(open) => setExpandedPid(open ? user.pid : null)}
          className="rounded-xl border border-border bg-card"
        >
          <CollapsibleTrigger className="flex w-full items-center gap-3 p-4 text-left">
            <UserAvatar src={user.avatarUrl} name={user.fullName} className="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{user.fullName}</p>
              {user.position && <p className="truncate text-xs text-muted-foreground">{user.position}</p>}
            </div>
            <Badge variant="secondary" className="gap-1.5 shrink-0">
              <ClipboardList className="h-3.5 w-3.5" />
              {taskCount} {taskCount === 1 ? "task" : "tasks"}
            </Badge>
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                expandedPid === user.pid && "rotate-180"
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="border-t border-border px-4 pb-4 pt-3">
            {tasks.length === 0 ? (
              <p className="text-xs text-muted-foreground">No task details available.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {tasks.map((task) => {
                  const meta = getPriorityStatusMeta(task.status);
                  return (
                    <li key={task.pid} className="flex items-center justify-between gap-2 text-sm">
                      <Link
                        to={`/priorities/${task.pid}`}
                        className="min-w-0 flex-1 truncate hover:underline"
                      >
                        <span className="mr-1.5 font-mono text-xs text-muted-foreground">{task.code}</span>
                        {task.name}
                      </Link>
                      <Badge variant="outline" className={cn("shrink-0 gap-1", meta.className)}>
                        <meta.icon className="h-3 w-3" />
                        {meta.label}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
}
